import clsx from 'clsx';
import React, { useRef } from 'react';
import { components } from 'react-select';

/**
 * Declared here rather than inherited from react-select: the version bay-ui resolves
 * against may not have the prop at all -- see the note on `useClassNamesCompat`.
 */
export type ClassNamesConfigCompat = Partial<Record<string, (state: any) => string | undefined>>;

// What an adapter renders: the component it wraps, and the callback that produces the
// class to merge in. Held in a ref so the adapter itself never changes type.
interface ClassNameSlot {
	Component: React.ComponentType<any>;
	getClassName: (state: any) => string | undefined;
}

type ClassNameSlots = Record<string, ClassNameSlot>;

// The `classNames` keys are the camelCase of react-select's own component names,
// with one exception: `container` is rendered by `SelectContainer`.
const COMPONENT_NAMES: Record<string, string> = {
	container: 'SelectContainer',
};

const componentNameFor = (key: string) =>
	COMPONENT_NAMES[key] || key.charAt(0).toUpperCase() + key.slice(1);

// The wrapper handed to react-select. It reads the component it wraps and the callback
// it calls out of `slots` on every render, so a later `applyClassNames` retargets this
// same component type instead of minting a new one -- react-select renders every entry
// of its `components` map as a JSX *type*, and a new type remounts the real DOM node.
const createClassNameAdapter = (
	slots: React.MutableRefObject<ClassNameSlots>,
	name: string,
) => {
	const ClassNameAdapter = (componentProps: any) => {
		const { Component: Target, getClassName } = slots.current[name];

		return (
			<Target
				{...componentProps}
				className={clsx(componentProps.className, getClassName(componentProps))}
			/>
		);
	};

	return ClassNameAdapter;
};

/**
 * react-select's `classNames` prop only landed in 5.5.0, and bay-ui cannot pin the
 * copy it runs against -- consumers build the submodule from source, so the host app
 * resolves it (smc pins 5.4.0). On older versions the prop is ignored with no error
 * and every rule written against those classes is dead CSS. This re-routes them
 * through the components' own `className`, which has merged since v2. On 5.5+ the
 * class simply arrives twice, which costs nothing.
 *
 * Returns the function to run over a select's `components` map -- call it last, so it
 * wraps whichever component that map settled on rather than being overwritten.
 */
const useClassNamesCompat = (classNames?: ClassNamesConfigCompat) => {
	// One adapter per slot name, created on first use and never replaced. The component
	// it wraps and the callback it calls arrive through `slots`.
	const slots = useRef<ClassNameSlots>({});
	const adapters = useRef<Record<string, React.ComponentType<any>>>({});

	const withClassName = (
		name: string,
		Component: React.ComponentType<any>,
		getClassName: (state: any) => string | undefined,
	) => {
		slots.current[name] = { Component, getClassName };

		if (!adapters.current[name]) {
			adapters.current[name] = createClassNameAdapter(slots, name);
		}

		return adapters.current[name];
	};

	return (built: Record<string, any>) => {
		if (!classNames) {
			return built;
		}

		return Object.entries(classNames).reduce((acc, [key, getClassName]) => {
			if (typeof getClassName !== 'function') {
				return acc;
			}

			const name = componentNameFor(key);
			// Whatever this select already settled on for the slot, falling back to
			// react-select's stock component only when nothing overrode it.
			const Component = acc[name] || (components as Record<string, any>)[name];

			if (!Component) {
				return acc;
			}

			return { ...acc, [name]: withClassName(name, Component, getClassName) };
		}, { ...built } as Record<string, any>);
	};
};

export default useClassNamesCompat;
